import { ControllerFlowAPI } from '@wix/yoshi-flow-editor';
import { queryPersonaChats } from '@wix/ambassador-legends-persona-v1-chat/http';
import {
  ChatMode,
  PersonaChat,
  SortOrder,
} from '@wix/ambassador-legends-persona-v1-chat/types';
import { shouldReportErrorMonitor } from '../../Challenge/helpers/shouldReportErrorMonitor';
import { handleError } from '../../../ErrorHandler/errorHandlerPropsMap';
import { EDUCATION_NAMESPACE } from '../consts';
import { request } from '../../../../services/request';

export const RECENT_CHATS_LIMIT = 10;
// Untitled chats are hidden, so pages are read until the list is full; the
// page cap bounds the requests when most chats are untitled
const QUERY_CHATS_LIMIT = 20;
const MAX_CHAT_PAGES = 3;

export interface RecentChat {
  id: string;
  // The title Legends generates after the first learner message, trimmed
  title: string;
  createdDate: Date | string | null;
}

// Returns null when the query fails, so the UI can tell it apart from "no chats"
export default async function (
  flowAPI: ControllerFlowAPI,
  personaId: string,
): Promise<RecentChat[] | null> {
  try {
    const transaction =
      flowAPI.panoramaClient.transaction('query-recent-chats');

    transaction.start();

    const recentChats: RecentChat[] = [];
    let cursor: string | null | undefined;

    for (let page = 0; page < MAX_CHAT_PAGES; page++) {
      const response = await request({
        flowAPI,
        options: queryPersonaChats({
          namespaceV2: EDUCATION_NAMESPACE,
          personaId,
          // A cursor carries the filter and sort of the first page
          query: cursor
            ? { cursorPaging: { limit: QUERY_CHATS_LIMIT, cursor } }
            : {
                filter: {
                  mode: ChatMode.PUBLISHED,
                },
                sort: [{ fieldName: 'createdDate', order: SortOrder.DESC }],
                cursorPaging: {
                  limit: QUERY_CHATS_LIMIT,
                },
              },
        }),
        errorOptions: {
          errorCodesMap: {
            applicationError: {},
          },
        },
      });

      const chats = response?.data?.chats ?? [];
      recentChats.push(...getTitledChats(chats));

      cursor = response?.data?.pagingMetadata?.cursors?.next;
      const hasNext = response?.data?.pagingMetadata?.hasNext && cursor;
      if (recentChats.length >= RECENT_CHATS_LIMIT || !hasNext) {
        break;
      }
    }

    transaction.finish();

    return recentChats.slice(0, RECENT_CHATS_LIMIT);
  } catch (error) {
    const resolvedError = flowAPI.errorHandler.getResolvedError(error);

    handleError({
      error,
      resolvedError,
      context: 'QueryRecentChats',
      preventErrorMonitorReport: !shouldReportErrorMonitor(error),
    });

    return null;
  }
}

// Legends titles a chat after the learner's first message and may defer it, so
// an untitled chat is either empty (every panel open creates one) or not named
// yet; neither is listed
function getTitledChats(chats: PersonaChat[]): RecentChat[] {
  return chats.flatMap((chat) => {
    const title = chat.title?.trim();

    return title
      ? [{ id: chat.id, title, createdDate: chat.createdDate ?? null }]
      : [];
  });
}
